<template>
	<div>
		<v-main>
			<v-container fluid class="profile-page">
				<div class="pr-head mb-4">
					<div class="pr-head-title">Profilo</div>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="$router.go(-1)"><v-icon left>mdi-keyboard-backspace</v-icon>Indietro</v-btn>
				</div>

				<!-- riepilogo -->
				<v-card outlined class="pr-summary mb-4">
					<v-avatar color="primary" size="56">
						<span class="white--text text-h6">{{ getAvatarShort(getName || '') }}</span>
					</v-avatar>
					<div class="pr-main">
						<div class="pr-title">{{ getName || 'Utente' }}</div>
						<div class="pr-sub">
							{{ getEmail }}<template v-if="nomeProfilo"> · {{ nomeProfilo }}</template>
						</div>
						<div class="pr-chips">
							<v-chip v-if="getRole === 0" color="error" label x-small text-color="white">SuperAdmin</v-chip>
							<v-chip v-else-if="getRole === 1" color="primary" label x-small text-color="white">Amministratore</v-chip>
							<v-chip v-else-if="getRole === 2" color="primary" label x-small text-color="white">Dipendente</v-chip>
						</div>
					</div>
				</v-card>

				<v-row class="pr-cols">
					<!-- colonna larga: dati e password (un solo Salva, come prima) -->
					<v-col cols="12" md="7">
						<SectionCard title="Account" icon="mdi-account-outline" class="mb-4">
							<v-row class="pr-fields">
								<v-col cols="12" sm="6">
									<v-text-field v-model="form.name" label="Nome" outlined dense hide-details="auto" :rules="validation.textRules" :error-messages="errors.name"></v-text-field>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.email"
										label="Email"
										prepend-inner-icon="mdi-lock-outline"
										hint="L'email di accesso non si modifica da qui"
										persistent-hint
										outlined
										dense
										disabled
										readonly
									></v-text-field>
								</v-col>
							</v-row>

							<div class="pr-section mt-3">Cambia password <span class="pr-optional">(facoltativo)</span></div>
							<v-row class="pr-fields">
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.password_new"
										label="Nuova password"
										autocomplete="new-password"
										:append-icon="showPassword2 ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
										:type="showPassword2 ? 'text' : 'password'"
										outlined
										dense
										hide-details="auto"
										:error-messages="errors.password_new"
										@click:append="showPassword2 = !showPassword2"
									></v-text-field>
									<div v-if="form.password_new" class="pr-strength mt-1">
										<div class="pr-strength-bar"><span :style="{ width: forza.percentuale + '%', background: forza.colore }"></span></div>
										<span class="pr-strength-label" :style="{ color: forza.colore }">{{ forza.testo }}</span>
									</div>
								</v-col>
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.password_new_confirm"
										label="Conferma nuova password"
										autocomplete="new-password"
										:append-icon="showPassword3 ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
										:type="showPassword3 ? 'text' : 'password'"
										outlined
										dense
										hide-details="auto"
										:rules="validation.passwordMatch"
										:error-messages="errors.password_new_confirm"
										@click:append="showPassword3 = !showPassword3"
									>
										<template v-if="form.password_new_confirm && form.password_new_confirm === form.password_new" #prepend-inner>
											<v-icon small color="success">mdi-check-circle</v-icon>
										</template>
									</v-text-field>
								</v-col>
							</v-row>
							<div class="pr-hint mt-1">Cambiando la password, gli altri dispositivi collegati vengono disconnessi.</div>

							<div class="pr-section mt-4">Conferma con la password attuale</div>
							<v-row class="pr-fields">
								<v-col cols="12" sm="6">
									<v-text-field
										v-model="form.password"
										label="Password attuale"
										autocomplete="current-password"
										:append-icon="showPassword1 ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
										:type="showPassword1 ? 'text' : 'password'"
										outlined
										dense
										hide-details="auto"
										:error-messages="errors.password"
										@click:append="showPassword1 = !showPassword1"
										@keyup.enter="save"
									></v-text-field>
								</v-col>
								<v-col cols="12" sm="6" class="d-flex align-center justify-end">
									<v-btn depressed color="primary" @click="save"><v-icon left>mdi-content-save-outline</v-icon>Salva</v-btn>
								</v-col>
							</v-row>
							<div class="pr-hint">Per salvare qualsiasi modifica, anche solo il nome, serve la password attuale.</div>
						</SectionCard>
					</v-col>

					<!-- colonna stretta: sicurezza -->
					<v-col cols="12" md="5">
						<two-factor-card class="mb-4" @notify="onNotify" />
						<SectionCard title="Sessioni" icon="mdi-devices" class="mb-4">
							<p class="pr-hint mb-3">Chiude l'accesso su tutti gli altri dispositivi e browser. Questo dispositivo resta collegato.</p>
							<v-btn outlined color="primary" @click="logoutAllDialog = true">
								<v-icon left>mdi-logout-variant</v-icon>
								Esci da tutti gli altri dispositivi
							</v-btn>
						</SectionCard>
					</v-col>
				</v-row>

				<v-dialog v-model="logoutAllDialog" max-width="420">
					<v-card>
						<v-card-title class="text-h6">Esci da tutti gli altri dispositivi?</v-card-title>
						<v-card-text>Tutte le altre sessioni aperte con questo utente verranno chiuse e dovranno rifare il login.</v-card-text>
						<v-card-actions>
							<v-spacer></v-spacer>
							<v-btn text @click="logoutAllDialog = false">Annulla</v-btn>
							<v-btn color="primary" :loading="logoutAllLoading" @click="confirmLogoutAll">Conferma</v-btn>
						</v-card-actions>
					</v-card>
				</v-dialog>
				<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout" :vertical="snackbar.mode === 'vertical'">
					{{ snackbar.text }}
					<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
				</v-snackbar>
			</v-container>
		</v-main>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import TwoFactorCard from './components/TwoFactorCard.vue';
import SectionCard from '@/components/core/SectionCard.vue';
export default {
	name: 'Profile',
	components: { TwoFactorCard, SectionCard },
	data() {
		return {
			check: true,
			logoutAllDialog: false,
			logoutAllLoading: false,
			showPassword1: false,
			showPassword2: false,
			showPassword3: false,
			form: {
				name: '',
				email: '',
				password: '',
				password_new: '',
				password_new_confirm: '',
			},
			errors: {
				name: '',
				password: '',
				password_new: '',
				password_new_confirm: '',
			},
			validation: {
				textRules: [v => !!v || this.$t('fillMandatory')],
				passwordMatch: [v => v == this.form.password_new || this.$t('password_not_match')],
			},
			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},
		};
	},
	created() {
		this.form.name = JSON.parse(JSON.stringify(this.getName));
		this.form.email = JSON.parse(JSON.stringify(this.getEmail));
	},
	methods: {
		...mapMutations({
			setPreload: 'settings/setPreload',
			setName: 'profile/setName',
			setSetting: 'settings/setSetting',
		}),
		...mapActions({
			updateProfile: 'profile/updateProfile',
			logoutAll: 'profile/logoutAll',
		}),
		getAvatarShort(name) {
			let split = name.split(' ');
			if (split.length > 1) {
				return split[0].charAt(0) + split[1].charAt(0);
			} else {
				return name.charAt(0).toUpperCase() + name.charAt(1).toUpperCase();
			}
		},

		async save() {
			await this.checkItem();
			if (!this.check) {
				return;
			} else {
				await this.resetError();
			}

			await this.setPreload(true);
			let result;
			try {
				result = await this.updateProfile(this.form);
			} catch (err) {
				// se la chiamata lancia, il preload non deve restare acceso
				result = (err && err.message) || 'Operazione non riuscita, riprova';
			} finally {
				await this.setPreload(false);
			}

			if (result.success) {
				if (this.form.password_new !== '') {
					// il backend ha revocato le altre sessioni e rimandato token nuovi per questa (salvati dallo store)
					this.snackbar.color = 'success';
					this.snackbar.text = 'Password modificata con successo. Gli altri dispositivi sono stati disconnessi';
					this.snackbar.snackbar = true;
					this.snackbar.mode = 'multi-line';
					this.snackbar.timeout = 5000;

					await this.setName(this.form.name);
					this.form.password = '';
					this.form.password_new = '';
					this.form.password_new_confirm = '';
				} else {
					this.snackbar.color = 'success';
					this.snackbar.text = 'Elemento aggiornato correttamente';
					this.snackbar.snackbar = true;
					this.snackbar.mode = 'multi-line';
					this.snackbar.timeout = 5000;

					await this.setName(this.form.name);
				}
			} else {
				let text = result;

				switch (result.message) {
					case 'credentials_not_valid': {
						text = 'La password attuale non è corretta';
					}
				}
				this.snackbar.color = 'error';
				this.snackbar.text = text;
				this.snackbar.snackbar = true;
				this.snackbar.mode = 'multi-line';
				this.snackbar.timeout = 5000;
			}
		},
		onNotify({ color, text }) {
			this.snackbar.color = color;
			this.snackbar.text = text;
			this.snackbar.snackbar = true;
			this.snackbar.mode = 'multi-line';
			this.snackbar.timeout = 5000;
		},
		async confirmLogoutAll() {
			this.logoutAllLoading = true;
			const result = await this.logoutAll();
			this.logoutAllLoading = false;
			this.logoutAllDialog = false;

			this.snackbar.color = result.success ? 'success' : 'error';
			this.snackbar.text = result.success ? 'Sei stato disconnesso da tutti gli altri dispositivi' : 'Operazione non riuscita, riprova';
			this.snackbar.snackbar = true;
			this.snackbar.mode = 'multi-line';
			this.snackbar.timeout = 5000;
		},
		async checkItem() {
			const error_mandatory = 'Campo obbligatorio';
			this.check = true;
			// a ogni tentativo si ripartono da zero: un errore gia' corretto non resta sotto il campo
			await this.resetError();

			if (!this.form.name) {
				this.check = false;
				this.errors.name = error_mandatory;
			}

			if (!this.form.password) {
				this.check = false;
				this.errors.password = error_mandatory;
			}

			if (this.form.password_new !== '' || this.form.password_new_confirm !== '') {
				if (!this.form.password_new) {
					this.check = false;
					this.errors.password_new = error_mandatory;
				}

				if (!this.form.password_new_confirm) {
					this.check = false;
					this.errors.password_new_confirm = error_mandatory;
				}

				if (this.form.password_new !== this.form.password_new_confirm) {
					this.check = false;
					this.errors.password_new_confirm = 'Le password non coincidono';
				}
			}
		},
		async resetError() {
			this.errors = {
				name: '',
				password: '',
				password_new: '',
				password_new_confirm: '',
			};
		},
	},
	computed: {
		// getProfile e' spesso l'_id del profilo: un ObjectId non dice niente all'utente e non si mostra
		nomeProfilo() {
			const p = this.getProfile;
			return p && !/^[a-f0-9]{24}$/i.test(String(p)) ? p : '';
		},
		// indicatore della robustezza della nuova password: solo grafica, nessun vincolo sul salvataggio
		forza() {
			const p = this.form.password_new || '';
			let punti = 0;
			if (p.length >= 8) punti++;
			if (p.length >= 12) punti++;
			if (/[a-z]/.test(p) && /[A-Z]/.test(p)) punti++;
			if (/\d/.test(p)) punti++;
			if (/[^A-Za-z0-9]/.test(p)) punti++;
			if (punti <= 1) return { percentuale: 25, colore: '#d32f2f', testo: 'Debole' };
			if (punti <= 3) return { percentuale: 60, colore: '#f0a020', testo: 'Discreta' };
			return { percentuale: 100, colore: '#2e7d32', testo: 'Robusta' };
		},
		...mapGetters({
			getName: 'profile/getName',
			getEmail: 'profile/getEmail',
			getRole: 'profile/getRole',
			getProfile: 'profile/getProfile',
		}),
	},
};
</script>

<style scoped>
.pr-head {
	display: flex;
	align-items: center;
}
.pr-head-title {
	font-size: 20px;
	font-weight: 500;
	color: #1f2d3d;
}
.pr-summary {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px 20px;
	padding: 12px 16px;
	border-radius: 10px !important;
}
.pr-main {
	flex: 1 1 240px;
	min-width: 0;
}
.pr-title {
	font-size: 17px;
	font-weight: 600;
	color: #1f2d3d;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.pr-sub {
	font-size: 13px;
	color: #5f6b7a;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.pr-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 4px;
}
.profile-page ::v-deep .section-card {
	height: auto;
}
.pr-cols {
	margin-top: 0;
	margin-bottom: 0;
}
.pr-cols > [class*='col'] {
	padding-top: 0;
	padding-bottom: 0;
}
.pr-fields {
	margin: 0 -8px;
}
.pr-fields > [class*='col'] {
	padding: 8px;
}
.pr-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
}
.pr-optional {
	text-transform: none;
	font-weight: 400;
	letter-spacing: 0;
}
.pr-hint {
	font-size: 12px;
	color: #5f6b7a;
}
.pr-strength {
	display: flex;
	align-items: center;
	gap: 8px;
}
.pr-strength-bar {
	flex: 1 1 auto;
	height: 4px;
	border-radius: 2px;
	background: #e1e7ef;
	overflow: hidden;
}
.pr-strength-bar span {
	display: block;
	height: 100%;
	transition: width 0.2s ease;
}
.pr-strength-label {
	font-size: 11px;
	font-weight: 600;
}
</style>
